import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import moment from "moment-timezone";
import React, { Fragment, useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import Select from "react-select";
import { withRouter } from "react-router-dom";

import { isUserAdmin, isUserPCP } from "@/utils/authToken";
import { sharedRequests } from "@/api/(requests)/shared";

import { BackToPatientProfile } from "../../Common/BackToPatientProfile";
import Badge from "../../UI/Badge";
import { Button, ButtonSizes, ButtonTypes } from "../../../component-library";
import { fetchCrisisRiskPatients } from "../../../redux/actions/reports";
import { formatMobileNumber, getQueryParams, toTitleCase } from "../../../utils/filters";
import { getAutomationDataAttr } from "../../../utils/automationUtils";
import { getReportPresignedUrl, viewReport } from "../../../utils/fileUtil";
import { hideLoader, showLoader } from "../../../redux/actions/loader";
import ImageFullPreview from "../../Common/ImageFullPreview";
import images from "../../../utils/images";
import {
  List,
  ListBody,
  ListHeader,
  ListItem,
  ListTitleBar,
  TitleBarItem,
} from "../../UI/ListView";
import { providerRoute } from "../../../config/roles";
import { showAlertWithAction } from "../../../redux/actions/alerts";

import "./styles.scss";

const IN_PROGRESS = "In-Progress";
const COMPLETE = "Complete";
const FAILED = "Failed";
const Spinner = () => <div className="loader"></div>;

const A5_REPORT = "A5";
const A5_REPEAT_REPORT = "A5-R";

const ReportList = (props) => {
  const {
    allReports,
    clearRetryInterval,
    dispatch,
    fetchAdminReportList,
    fetchPatientReportList,
    patientName,
    patientReportsNextToken,
    practiceId,
    practiceIdChangeHandler,
    practiceOptions,
    providerReportsNextToken,
    reportList,
    reportType,
    reportTypeChangeHandler,
    searchHandler,
    selectedPractice,
    setPatientReportsNextToken,
    setProviderReportsNextToken,
    setReportList,
  } = props;

  const patientId = getQueryParams("patient-id", props.location.search);

  const [patientChecked, setPatientChecked] = useState(true);
  const [adminChecked, setAdminChecked] = useState(false);
  const [crisisRiskChecked, setCrisisRiskChecked] = useState(false);
  const [progressNotes, setProgressNotes] = useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [scanNotesForward, setScanNotesForward] = useState(false);
  const [showReportModal, setShowReportModal] = useState(null);
  const [crisisRiskPracticeId, setCrisisRiskPracticeId] = useState(undefined);
  const [crisisRiskReportList, setCrisisRiskReportList] = useState([]);
  const [crisisRiskLastEvaluatedKey, setCrisisRiskLastEvaluatedKey] = useState(undefined);
  const [crisisScoreSource, setCrisisScoreSource] = useState(A5_REPORT);

  const reportDropdownOption = {
    label: "Show Report Activity",
    value: "reports",
  };

  const noteDropdownOption = {
    label: "Show Progress Note Activity",
    value: "notes",
  };

  const a5ReportDropdownOption = {
    label: A5_REPORT,
    value: A5_REPORT,
  };
  const a5RepeatReportDropdownOption = {
    label: A5_REPEAT_REPORT,
    value: A5_REPEAT_REPORT,
  };

  const defaultPatientReportOption = () => {
    if (isUserPCP() || isUserAdmin()) {
      return reportDropdownOption;
    } else {
      return noteDropdownOption;
    }
  };

  const [viewOption, setViewOption] = useState(defaultPatientReportOption());

  useEffect(() => {
    (async () => {
      await fetchNotes();
    })();
  }, [viewOption, practiceId, scanNotesForward, adminChecked]);

  const fetchNotes = async (lastEvaluatedKey) => {
    if (viewOption.value === "notes" && !adminChecked) {
      let notes = [];

      if (isUserAdmin()) {
        if (practiceId !== "all_practices") {
          notes = await fetchProgressNotesByPractice(practiceId, lastEvaluatedKey);
        }
      } else if (isUserPCP()) {
        notes = await fetchProgressNotesByPCPPractice(props.profile.practice_id, lastEvaluatedKey);
      } else {
        notes = await fetchProgressNotesByProvider(props.profile.username, lastEvaluatedKey);
      }

      if (!notes) {
        notes = [];
      }

      notes = notes.filter(
        (note) =>
          note["note_type"] !== "Initial Clinical Assessment" &&
          note["note_type"] !== "Outcome Clinical Assessment" &&
          note["note_type"] !== "Discharge",
      );
      setProgressNotes(notes);
    } else {
      setProgressNotes([]);
      setLastEvaluatedKey(null);
    }
  };

  const fetchProgressNotesByPractice = async (practiceId, lastEvaluatedKey) => {
    const searchParams = {
      practiceId: practiceId,
    };

    let data = {
      scan_forward: scanNotesForward,
    };

    if (lastEvaluatedKey) {
      data["last_evaluated_key"] = lastEvaluatedKey;
    }
    const response = await sharedRequests.get_progress_notes_by_practice_id({ data, searchParams });

    if (response.results.length === 0) {
      return;
    }

    setLastEvaluatedKey(response.last_evaluated_key);

    return [...progressNotes, ...response.results];
  };

  const fetchProgressNotesByPCPPractice = async (practiceId, lastEvaluatedKey) => {
    const searchParams = {
      pcpPracticeId: practiceId,
    };

    let data = {
      scan_forward: scanNotesForward,
    };

    if (lastEvaluatedKey) {
      data["last_evaluated_key"] = lastEvaluatedKey;
    }
    const response = await sharedRequests.get_progress_notes_by_pcp_practice_id({
      data,
      searchParams,
    });

    if (response.results.length === 0) {
      return;
    }

    setLastEvaluatedKey(response.last_evaluated_key);

    return [...progressNotes, ...response.results];
  };

  const fetchProgressNotesByProvider = async (providerId, lastEvaluatedKey) => {
    const searchParams = {
      providerId: providerId,
    };

    let data = {
      scan_forward: scanNotesForward,
    };

    if (lastEvaluatedKey) {
      data["last_evaluated_key"] = lastEvaluatedKey;
    }
    const response = await sharedRequests.get_progress_notes_by_provider_id({ data, searchParams });

    if (response.results.length === 0) {
      return;
    }

    setLastEvaluatedKey(response.last_evaluated_key);

    return [...progressNotes, ...response.results];
  };

  const addNewAdminReport = () => {
    let url = "/app/reports/add";

    if (isUserAdmin()) {
      url += "?practiceId=" + practiceId;
    }
    props.history.push(url);
  };

  const crisisRiskPracticeChangeHandler = (e) => {
    if (!e) {
      return;
    }

    setCrisisRiskPracticeId(e.value);
  };

  const getCrisisRiskReport = async ({
    lastEvaluatedKey = crisisRiskLastEvaluatedKey,
    practiceId = crisisRiskPracticeId,
    sourceReport = crisisScoreSource,
  }) => {
    try {
      dispatch(showLoader());
      const data = {
        assessment_type: sourceReport,
        last_evaluated_key: lastEvaluatedKey ? lastEvaluatedKey : undefined,
        practice_id: practiceId ? practiceId : undefined,
      };
      const response = await fetchCrisisRiskPatients({
        context: { showLoader: false },
        data,
        dispatch,
      });

      const crisisRiskReportResponseList = response.reports;

      const shouldRetainReportList = practiceId === crisisRiskPracticeId && lastEvaluatedKey;
      const currentDisplayedCrisisRisks = shouldRetainReportList ? crisisRiskReportList : [];

      setCrisisRiskReportList([...currentDisplayedCrisisRisks, ...crisisRiskReportResponseList]);
      setCrisisRiskLastEvaluatedKey(response.last_evaluated_key);
      setCrisisRiskPracticeId(practiceId);
      dispatch(hideLoader());
    } catch (error) {
      console.error(error);
    }
  };

  const reportTypeChangeHanderWithCrisisRisk = (e) => {
    let reportType = e.value;

    if (!reportType) {
      reportType = e.target.value;
    }
    if (reportType === "crisis_risk") {
      setCrisisRiskChecked(true);
      setAdminChecked(false);
      setPatientChecked(false);
      getCrisisRiskReport(e);
    } else {
      reportTypeChangeHandler(e);
    }
  };

  let addAdminReportEnabled =
    (reportType === "admin" &&
      isUserAdmin() &&
      practiceId !== "all_practices" &&
      practiceId != null) ||
    !isUserAdmin();

  const buildPatientReportOptions = () => {
    const options = [noteDropdownOption];

    if (isUserAdmin() || isUserPCP()) {
      options.unshift(reportDropdownOption);
    }

    return options;
  };

  // ListView TitleBar controls
  const renderControls = () => {
    return (
      /*DESKTOP*/
      <div className="d-sm-flex d-none flex-column flex-lg-row align-items-lg-center justify-content-lg-end">
        {isUserAdmin() && practiceOptions && (
          <div className="d-flex align-items-center">
            <span className="mr-2 tw:font-bold">Select Your Practice: </span>
            <div className="mr-lg-4" style={{ width: 280 }}>
              <Select
                className="mr-lg-2 practice-search"
                isSearchable={true}
                onChange={(e) => {
                  setReportList([]);
                  setProgressNotes([]);
                  if (crisisRiskChecked) {
                    getCrisisRiskReport({
                      lastEvaluatedKey: null,
                      practiceId: e.value,
                    });
                  } else {
                    practiceIdChangeHandler(e);
                  }
                }}
                options={practiceOptions}
                placeholder="Search"
                value={selectedPractice}
              />
            </div>
          </div>
        )}
        {/*<SearchBar className="mr-lg-2" onchange={searchHandler} />*/}
        {adminChecked && (
          <Button
            buttonType={ButtonTypes.primaryV2}
            disabled={addAdminReportEnabled ? null : true}
            onClick={addNewAdminReport}
          >
            Create New
          </Button>
        )}
      </div>
      /*MOBILE*/
    );
  };

  const updateCrisisReportType = (reportName) => {
    setCrisisRiskLastEvaluatedKey(undefined);
    setCrisisRiskReportList([]);
    setCrisisScoreSource(reportName);
    getCrisisRiskReport({
      lastEvaluatedKey: null,
      sourceReport: reportName,
    });
  };

  const renderCrisisScoreSourceRadioButton = (reportName) => {
    return (
      <div className="CrisisScoreSourceOptions-radio--input">
        <input
          checked={crisisScoreSource === reportName}
          onChange={() => {
            updateCrisisReportType(reportName);
          }}
          type="radio"
          value={reportName}
          {...getAutomationDataAttr(
            `report-list-crisis-risk-${reportName.toLowerCase()}-type-radio-button`,
          )}
        />
        <span className="CrisisScoreSourceOptions-radio--input-label">{reportName}</span>
      </div>
    );
  };

  const renderCrisisScoreSourceRadioButtonGroup = () => {
    return (
      <div className="CrisisScoreSourceOptions-radio mx-lg-4">
        <span className="CrisisScoreSourceOptions-radio--group-name">Triggered by:</span>
        {renderCrisisScoreSourceRadioButton(A5_REPORT)}
        {renderCrisisScoreSourceRadioButton(A5_REPEAT_REPORT)}
      </div>
    );
  };

  const renderCrisisScoreSourceSelect = () => {
    return (
      <div className="CrisisScoreSourceOptions-select">
        <span className="CrisisScoreSourceOptions-select--label">Triggered by</span>
        <Select
          onChange={(option) => {
            updateCrisisReportType(option.value);
          }}
          options={[a5ReportDropdownOption, a5RepeatReportDropdownOption]}
          value={{ label: crisisScoreSource, value: crisisScoreSource }}
        />
      </div>
    );
  };

  const renderCrisisScoreSourceOptions = () => {
    return (
      <div className="CrisisScoreSourceOptions" data-public="true">
        {renderCrisisScoreSourceRadioButtonGroup()}
        {renderCrisisScoreSourceSelect()}
        <div className="CrisisScoreSourceOptions-explanation mx-lg-4">
          Note that the risk score calculation and scale varies between A5 and A5-R
        </div>
      </div>
    );
  };

  const renderSecondaryControls = () => {
    return (
      <>
        {/*DESKTOP*/}
        <div
          className="d-sm-flex d-none align-items-lg-center justify-content-lg-between"
          data-public="true"
          style={{
            borderBottom: "1px solid #AEAEAE",
            borderTop: "1px solid #AEAEAE",
            padding: 4,
          }}
        >
          <div
            className="mx-lg-4"
            style={{ alignItems: "center", display: "flex", height: 50, width: 490 }}
          >
            <span className="tw:font-bold" style={{ width: "115px" }}>
              Report View:{" "}
            </span>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                width: 150,
              }}
            >
              <input
                checked={patientChecked}
                onChange={(e) => {
                  if (clearRetryInterval) {
                    clearInterval(clearRetryInterval);
                  }
                  setCrisisRiskChecked(false);
                  setPatientChecked(true);
                  setAdminChecked(false);
                  setLastEvaluatedKey(null);
                  setProviderReportsNextToken(null);
                  setPatientReportsNextToken(null);
                  setReportList([]);

                  if (viewOption.value !== "notes") {
                    reportTypeChangeHandler(e);
                  }
                }}
                type="radio"
                value="patient"
              />
              <span style={{ marginLeft: "10px" }}>Patients</span>
            </div>
            {isUserAdmin() && (
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  width: 150,
                }}
              >
                <input
                  checked={adminChecked}
                  onChange={(e) => {
                    setCrisisRiskChecked(false);
                    setAdminChecked(true);
                    setPatientChecked(false);
                    setLastEvaluatedKey(null);
                    setProviderReportsNextToken(null);
                    setPatientReportsNextToken(null);
                    setReportList([]);

                    reportTypeChangeHandler(e);
                  }}
                  type="radio"
                  value="admin"
                />
                <span style={{ marginLeft: "10px" }}>Administration</span>
              </div>
            )}
            {(isUserAdmin() || props.profile?.is_universal_access_enabled) && (
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  marginLeft: 10,
                  width: 150,
                }}
              >
                <input
                  checked={crisisRiskChecked}
                  onChange={(e) => {
                    setCrisisRiskChecked(true);
                    setAdminChecked(false);
                    setPatientChecked(false);
                    setLastEvaluatedKey(null);
                    setProviderReportsNextToken(null);
                    setPatientReportsNextToken(null);
                    setReportList([]);
                    setCrisisRiskReportList([]);
                    setCrisisRiskLastEvaluatedKey(null);

                    getCrisisRiskReport(e);
                  }}
                  type="radio"
                  value="crisis-risk"
                  {...getAutomationDataAttr("report-list-crisis-risk-radio-button")}
                />
                <span style={{ marginLeft: "10px" }}>Crisis Risk</span>
              </div>
            )}
          </div>
          {!crisisRiskChecked && !adminChecked && (
            <Select
              className="mx-lg-4 reports-select"
              onChange={(option) => {
                setViewOption(option);
                setLastEvaluatedKey(null);
                setProviderReportsNextToken(null);
                setPatientReportsNextToken(null);
                setReportList([]);
              }}
              options={buildPatientReportOptions()}
              value={viewOption}
            />
          )}
        </div>
        {/*MOBILE*/}
        <div
          className="d-sm-none d-flex align-items-center justify-content-between"
          style={{ width: "100vw" }}
        >
          <div className="practice-search-small">
            <span className="fs-14 tw:font-bold">Report View</span>
            <Select
              className="w-100"
              onChange={(option) => {
                setViewOption(option);
                setLastEvaluatedKey(null);
              }}
              options={[
                {
                  label: "Show Report Activity",
                  value: "reports",
                },
                {
                  label: "Show Progress Note Activity",
                  value: "notes",
                },
              ]}
              value={viewOption}
            />
          </div>
          <div className="practice-search-small">
            <span className="fs-14 tw:font-bold">Type</span>
            <Select
              className="w-100"
              onChange={reportTypeChangeHanderWithCrisisRisk}
              onSelect={reportTypeChangeHanderWithCrisisRisk}
              options={[
                {
                  label: "Patient Report",
                  value: "patient",
                },
                {
                  label: "Administrator Report",
                  value: "admin",
                },
                {
                  label: "Crisis Risk Report",
                  value: "crisis_risk",
                },
              ]}
              placeholder="Search"
            />
          </div>
        </div>
        {isUserAdmin() && practiceOptions && !crisisRiskChecked && (
          <div
            className="d-sm-none d-flex mt-2 mb-2 flex-column justify-content-between"
            style={{ width: "100vw" }}
          >
            <span className="fs-14 tw:font-bold">Practice</span>
            <Select
              className="practice-search-mobile"
              onChange={practiceIdChangeHandler}
              onSelect={practiceIdChangeHandler}
              options={practiceOptions}
              placeholder="Search"
            />
          </div>
        )}
        {isUserAdmin() && practiceOptions && crisisRiskChecked && (
          <div
            className="d-sm-none d-flex mt-2 mb-2 flex-column justify-content-between"
            style={{ width: "100vw" }}
          >
            <span className="fs-14 tw:font-bold">Practice</span>
            <Select
              className="practice-search-mobile crisis-risk"
              onChange={crisisRiskPracticeChangeHandler}
              onSelect={crisisRiskPracticeChangeHandler}
              options={practiceOptions}
              placeholder="Search"
            />
          </div>
        )}
        {addAdminReportEnabled && viewOption.value !== "notes" && !crisisRiskChecked && (
          <div className="circle d-sm-none d-flex" onClick={addNewAdminReport} role="button">
            <img
              alt="add new report"
              className="img-fluid plus"
              src={images("./icons/add-white-btn.svg")}
            />
          </div>
        )}
      </>
    );
  };

  const showReport = async (report) => {
    let file_url = await getReportPresignedUrl(report);

    setShowReportModal(file_url);
  };

  const renderBadge = (status = "Not available") => {
    let type;

    switch (status) {
      case IN_PROGRESS:
        type = "progress";
        break;

      case COMPLETE:
        type = "success";
        break;

      case "Not available":
        type = "grey";
        break;

      default:
        type = "grey";
    }

    return <Badge text={status} type={type} />;
  };

  const renderBadgeNotes = (status) => {
    let type;

    switch (status) {
      case "draft":
        status = "Draft";
        type = "progress";
        break;

      default:
        status = "Complete";
        type = "success";
        break;
    }

    return <Badge text={status} type={type} />;
  };

  const archiveReport = async (id, timestamp) => {
    try {
      const data = { status: "ARCHIVED", timestamp: timestamp };
      const searchParams = {};

      searchParams["providerId"] = id;
      await sharedRequests.update_provider_report({ data, searchParams });
      const reportIndex = reportList.findIndex(
        (report) => report["id"] === id && report["timestamp"] === timestamp,
      );
      let newReportList = [...reportList];

      newReportList.splice(reportIndex, 1);
      setReportList(newReportList);
    } catch (err) {
      console.log(err);
    }
  };

  // A utility function to generate list items for mobile and desktop devices
  const getListChildren = (report, reportIndex) => {
    let {
      action,
      entity_id,
      first_name,
      id,
      last_name,
      modified_at,
      name,
      patient_id,
      report_url,
      status,
      timestamp,
      type,
    } = report;

    if (timestamp) {
      modified_at = moment.utc(timestamp).tz(props.timezone).format("MM/DD/YYYY hh:mm A");
    }
    let reportLink;

    function constructReportLink(type) {
      reportLink = `${providerRoute}/reports/${type}`;
    }

    switch (type) {
      case "I-BAR":
        constructReportLink("ibar");
        break;

      case "I-PSR":
        constructReportLink("ipsr");
        break;

      case "O-BAR":
        constructReportLink("obar");
        break;

      case "O-PSR":
        constructReportLink("opsr");
        break;

      default:
        reportLink = `${providerRoute}/reports`;
    }
    const patientName = `${first_name} ${last_name}`;

    return (
      <Fragment>
        {/* for mobile devices */}
        <div className="d-flex d-lg-none justify-content-between">
          <div className="w-75 d-flex fs-14 flex-column">
            <span className="fs-16">{modified_at}</span>
            {allReports && reportType === "patient" && (
              <span className="fs-16" data-private>
                {first_name && last_name
                  ? toTitleCase(first_name) + " " + toTitleCase(last_name)
                  : ""}
              </span>
            )}
            {allReports && reportType === "admin" && <span className="fs-16">{name}</span>}
            <span>{type}</span>
          </div>
          <div className="cursor d-flex ml-5 mr-2">
            {allReports && reportType === "patient" && (
              <span className="mr-3">{renderBadge(status)}</span>
            )}
            <a href={report_url} rel="noreferrer" target="_blank">
              <img
                alt="view icon"
                onClick={() => {
                  showReport(report);
                }}
                src={images("./common/view.png")}
              />
            </a>
          </div>
        </div>
        {/* for desktop */}
        <div className="row no-gutters d-none d-lg-flex fs-14 align-items-center justify-content-between">
          <span className="col-2">{modified_at}</span>
          {allReports && reportType === "patient" && (
            <span className="col-5" data-private>
              {first_name && last_name
                ? toTitleCase(first_name) + " " + toTitleCase(last_name)
                : ""}
            </span>
          )}
          {allReports && reportType === "admin" && <span className="col-5">{name}</span>}
          <span className="col-2">{type}</span>
          {allReports && reportType === "patient" && (
            <div className="col-1">{renderBadge(status)}</div>
          )}
          {allReports && reportType === "admin" && (
            <div className="col-1">
              <Button
                buttonSize={ButtonSizes.small}
                buttonType={ButtonTypes.primaryOutlineV2}
                className="fs-12"
                onClick={() => archiveReport(id, timestamp)}
                {...getAutomationDataAttr(`AdminReportList_archive_${reportIndex}`)}
              >
                Archive
              </Button>
            </div>
          )}
          <div className="col-2 d-flex d-flex tw:justify-center">
            {allReports && reportType === "admin" && (
              <div className="ml-5">{renderDownloadStatusIcon(report, reportIndex)}</div>
            )}
            {allReports && reportType === "patient" && (
              <div className="ml-5">{renderPatientReportDownloadLink(report)}</div>
            )}
            {allReports && reportType === "patient" && (
              <div className="cursor ml-5">
                <img
                  alt="view icon"
                  onClick={() => {
                    showReport(report);
                  }}
                  src={images("./common/view.png")}
                />
              </div>
            )}
          </div>
        </div>
      </Fragment>
    );
  };

  const renderDownloadStatusIcon = (report, reportIndex) => {
    const { multipart_report, report_part_urls, report_url, status } = report;
    let icon;
    let tooltipText;
    let linkClass = null;

    if (status === IN_PROGRESS && !report_url) {
      icon = <Spinner />;
      tooltipText = "Report Generating";

      return renderLinkWithTooltip(icon, tooltipText, null, null);
    } else if (status === FAILED) {
      icon = <img alt="error icon" src={images("./icons/error-icon.svg")} />;
      tooltipText = "Failed to Download";

      return renderLinkWithTooltip(icon, tooltipText, null, null);
    } else {
      tooltipText = "Ready to Download";
      linkClass = "cursor";
      if (multipart_report) {
        icon = <img alt="download icon" src={images("./icons/download-icon.svg")} />;

        return renderMultipartLinkWithTooltips(icon, tooltipText, report_part_urls, linkClass);
      } else {
        icon = <img alt="download icon" src={images("./icons/download-icon.svg")} />;

        return renderLinkWithTooltip(
          icon,
          tooltipText,
          report_url,
          linkClass,
          `AdminReportList_download_${reportIndex}`,
        );
      }
    }
  };

  const renderPatientReportDownloadLink = (report) => {
    if (!report.file_key) {
      return <></>;
    }

    const icon = <img alt="download icon" src={images("./icons/download-icon.svg")} />;

    return (
      <div
        onClick={async () => {
          await viewReport(report);
        }}
        role="button"
      >
        {icon}
      </div>
    );
  };

  const renderLinkWithTooltip = (icon, tooltipText, linkUrl, linkClass, testId) => {
    return (
      <>
        <a
          className={linkClass}
          data-for="downloadStatusLink"
          data-iscapture="true"
          data-tip={tooltipText}
          href={linkUrl}
          rel="noreferrer"
          target="_blank"
          {...getAutomationDataAttr(testId)}
        >
          {icon}
        </a>
        <ReactTooltip
          backgroundColor="#E0E0E0"
          effect="solid"
          id="downloadStatusLink"
          place="top"
          textColor="black"
        />
      </>
    );
  };

  const renderMultipartLinkWithTooltips = (icon, tooltipText, report_part_urls, linkClass) => {
    const retVal = [];

    report_part_urls.forEach((report_part_url, i) => {
      if (i !== 0) {
        retVal.push(<hr />);
      }
      retVal.push(
        <span className="d-inline-block mr-2">{`Part ${i + 1}/${report_part_urls.length}`}</span>,
        renderLinkWithTooltip(icon, tooltipText, report_part_urls[i], linkClass),
      );
    });

    return retVal;
  };

  // A utility function to generate list items for mobile and desktop devices
  const getListChildrenProgressNote = (note) => {
    let {
      id,
      note_state,
      patient_first_name,
      patient_id,
      patient_last_name,
      provider_type,
      session_date,
    } = note;
    const patientName = `${patient_first_name} ${patient_last_name}`;
    const providerType = provider_type === "PRESCRIBE" ? "PRESCRIBER" : "THERAPIST";

    return (
      <Fragment>
        {/* for mobile devices */}
        <div className="d-flex d-lg-none justify-content-between" onClick={() => {}}>
          <div className="w-75 d-flex fs-14 flex-column">
            <span className="fs-16" data-private>
              {patientName}
            </span>
            <span className="fs-16">{session_date}</span>
            <span className="fs-16">{providerType}</span>
          </div>
          <div className="flex-column d-flex ml-5">
            <span className="mr-3">{renderBadgeNotes(note_state)}</span>
          </div>
        </div>
        {/* for desktop */}
        <div className="row no-gutters d-none d-lg-flex fs-14 align-items-center justify-content-between">
          <span className="col-2">{session_date}</span>
          <span className="col-5" data-private>
            {patientName}
          </span>
          <span className="col-2">{providerType}</span>
          <div className="col-1">{renderBadgeNotes(note_state)}</div>
          <div className="col-2 d-flex d-flex tw:justify-center">
            <div className="cursor ml-5">
              <img
                alt="print icon"
                onClick={() =>
                  props.history.push(`/app/patient/${patient_id}/export_notes?noteIds=${id}`)
                }
                src={images("./icons/print.svg")}
                style={{ height: "45px" }}
              />
            </div>
            <div className="cursor ml-5 d-flex align-items-center tw:justify-center">
              <img
                alt="view icon"
                onClick={() => {
                  props.history.push(`/app/patient/${patient_id}/note/${id}/v2`);
                }}
                src={images("./common/view.png")}
              />
            </div>
          </div>
        </div>
      </Fragment>
    );
  };

  const getViewButton = (report) => {
    if (report?.segment_id) {
      report = transformCrisisRiskIntoReport(report);
    }

    return (
      <img
        alt="show"
        className="cursor view-report-icon"
        onClick={async () => {
          let file_url = await getReportPresignedUrl(report);

          setShowReportModal(file_url);
        }}
        src={images("./common/show.svg")}
      />
    );
  };

  const transformCrisisRiskIntoReport = (report) => {
    return {
      entity_id: report?.metadata?.patient_id,
      file_key: report?.report_key,
      file_sub_type: A5_REPORT,
      file_type: "CORE_REPORT",
    };
  };

  const getCrisisRiskInfo = (report) => {
    const { entity_id, iso_timestamp, metadata, rank, report_key, report_url } = report;
    const { created_date, first_name, last_name, mobile, patient_id } = metadata;

    const profileUrl = patient_id && `/app/patient/${patient_id}/profile/v2?selectedTab=Reports`;
    const completedDateTime = moment.utc(iso_timestamp).tz("America/New_York");

    return (
      <>
        {/* for mobile devices */}
        <div className="d-flex d-lg-none justify-content-between" onClick={() => {}}>
          <div className="w-75 d-flex fs-14 flex-column">
            <span className="fs-16">Score: {rank}</span>
            <span className="fs-16">{completedDateTime.format("MM/DD/YYYY h:mm A")}</span>
            <span className="fs-16" data-private>
              {first_name} {last_name} {formatMobileNumber(mobile)}
            </span>
          </div>
          <div
            className={`${
              profileUrl ? "w-25" : "w-10-percent"
            } cursor d-flex align-items-center justify-content-between ml-5 mr-2`}
          >
            {profileUrl && (
              <img
                alt="view icon"
                height="30"
                onClick={() => props.history.push(profileUrl)}
                src={images("./common/avatar.png")}
                width="30"
              />
            )}
            <a href={report_url} rel="noreferrer" target="_blank">
              <img alt="download icon" src={images("./icons/download-icon.svg")} />
            </a>
            {getViewButton(report)}
          </div>
        </div>
        {/* for desktop */}
        <div className="row no-gutters d-none d-lg-flex fs-14 align-items-center justify-content-between">
          <span className="col-1">{rank}</span>
          <span className="col-2">{completedDateTime.format("h:mm A")}</span>
          <span className="col-2">{completedDateTime.format("MM/DD/YYYY")}</span>
          <span className="col-3" data-private>
            {first_name} {last_name}
          </span>
          <div className="col-2">{formatMobileNumber(mobile)}</div>
          <div className="col-1 cursor">
            {profileUrl && (
              <img
                alt="view icon"
                onClick={() => props.history.push(profileUrl)}
                src={images("./common/avatar.png")}
                width="30"
              />
            )}
          </div>
          <div className="col-1 d-flex d-flex tw:justify-start">
            {report_key && (
              <>
                <div className="cursor">
                  <a href={report_url} rel="noreferrer" target="_blank">
                    <img
                      alt="download icon"
                      src={images("./icons/download-icon.svg")}
                      {...getAutomationDataAttr(`report-list-view-crisis-risk-${entity_id}`)}
                    />
                  </a>
                </div>
                <div className="cursor d-flex align-items-center ml-2">{getViewButton(report)}</div>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  const toggleNoteScanDirection = () => {
    if (viewOption.value === "notes") {
      setProgressNotes([]);
      setScanNotesForward(!scanNotesForward);
    }
  };

  let listItems = (
    <Fragment>
      {reportList.length > 0 &&
        reportList.map((report, index) => {
          let key = report.type;

          if (report.patient_id) {
            key += report.patient_id;
          }
          if (report.name) {
            key += report.name;
          }
          if (report.created_date) {
            key += report.created_date + Math.random();
          }

          return (
            <ListItem clickHandler={() => console.log("clicked")} clickOnMobile key={key}>
              {getListChildren(report, index)}
            </ListItem>
          );
        })}
    </Fragment>
  );

  if (viewOption.value === "notes" && !adminChecked) {
    if (progressNotes.length === 0) {
      listItems = null;
    } else {
      listItems = (
        <Fragment>
          {progressNotes.map((note) => {
            return (
              <ListItem className="smaller-list-item" key={note.id}>
                {getListChildrenProgressNote(note)}
              </ListItem>
            );
          })}
        </Fragment>
      );
    }
  }

  if (crisisRiskChecked) {
    listItems = (
      <>
        {crisisRiskReportList.length > 0 &&
          crisisRiskReportList.map((report) => {
            return (
              <ListItem className="smaller-list-item" key={report.patient_id}>
                {getCrisisRiskInfo(report)}
              </ListItem>
            );
          })}
      </>
    );
  }

  let sessionDate = (
    <div style={{ cursor: "pointer" }}>
      <span>Session Date</span> <img alt="sort" src={images("./common/sort_arrows.svg")} />
    </div>
  );

  return (
    <div>
      {!allReports && <BackToPatientProfile patientId={patientId} />}

      <ListBody>
        <ListHeader
          render={allReports ? renderControls : null}
          title={allReports ? "Reports" : patientName + "'s Reports"}
        />

        {allReports && renderSecondaryControls()}
        {crisisRiskChecked && renderCrisisScoreSourceOptions()}

        <ListTitleBar>
          {!crisisRiskChecked ? (
            <>
              <TitleBarItem
                classes="col-2"
                onClick={toggleNoteScanDirection}
                text={viewOption.value === "notes" ? sessionDate : "Date Modified"}
              />

              {allReports && (
                <TitleBarItem
                  classes="col-5"
                  text={reportType === "patient" ? "Patient Name" : "Name"}
                />
              )}

              <TitleBarItem classes="col-2" text="Report Type" />

              {allReports && reportType === "admin" && (
                <TitleBarItem classes="col-1" text="Archive" />
              )}

              {allReports && reportType === "patient" && (
                <TitleBarItem classes="col-1" text="Status" />
              )}
              <TitleBarItem classes="col-2" text="" />
            </>
          ) : (
            <>
              <TitleBarItem
                classes="col-1"
                text={
                  <>
                    Score
                    <br />({crisisScoreSource})
                  </>
                }
              />

              <TitleBarItem classes="col-2" text="Time (EST)" />
              <TitleBarItem classes="col-2" text="Date" />
              <TitleBarItem classes="col-3" text="Patient Name" />
              <TitleBarItem classes="col-2" text="Phone" />
              <TitleBarItem classes="col-1" text="Profile" />
              <TitleBarItem classes="col-1" text="Report" />
            </>
          )}
        </ListTitleBar>
        <List>{listItems}</List>
      </ListBody>

      {patientChecked && viewOption.value === "notes" && lastEvaluatedKey && (
        <div className="d-flex align-center mb-4 tw:justify-center">
          <Button
            buttonType={ButtonTypes.primaryV2}
            disabled={lastEvaluatedKey ? null : true}
            onClick={() => fetchNotes(lastEvaluatedKey)}
          >
            Load More Notes
          </Button>
        </div>
      )}

      {adminChecked && providerReportsNextToken && (
        <div className="d-flex align-center mb-4 tw:justify-center">
          <Button
            buttonType={ButtonTypes.primaryV2}
            disabled={providerReportsNextToken ? null : true}
            onClick={() => fetchAdminReportList(providerReportsNextToken)}
          >
            Load More Administration Reports
          </Button>
        </div>
      )}

      {patientChecked && viewOption.value === "reports" && patientReportsNextToken && (
        <div className="d-flex align-center mb-4 tw:justify-center">
          <Button
            buttonType={ButtonTypes.primaryV2}
            disabled={patientReportsNextToken ? null : true}
            onClick={() => fetchPatientReportList(practiceId, patientReportsNextToken)}
          >
            Load More Patient Reports
          </Button>
        </div>
      )}

      {crisisRiskChecked && crisisRiskLastEvaluatedKey && (
        <div className="d-flex align-center mb-4 tw:justify-center">
          <Button
            buttonType={ButtonTypes.primaryV2}
            disabled={crisisRiskLastEvaluatedKey ? null : true}
            onClick={getCrisisRiskReport}
          >
            Load More Crisis Risk Reports
          </Button>
        </div>
      )}

      {showReportModal && (
        <ImageFullPreview
          close={() => setShowReportModal(false)}
          contentType="pdf"
          url={showReportModal}
          wideView={true}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    profile: state.auth.profile,
    timezone: state.auth.timezone,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    dispatch,
    ...bindActionCreators({ showAlertWithAction }, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ReportList));
